// QuestionScreen.js - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Mic,
  MicOff,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  CheckCircle,
  ChevronDown,
  Send,
  FileText,
  ArrowRight,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import ProgressRing from "./ProgressRing";
import VoiceVisualizer from "./VoiceVisualizer";
import LoadingScreen from "./LoadingScreen";
import { questionsConfig, categories } from "../data/assessmentData";

const LS_CATEGORY_REPORTS_KEY = "adhd_category_reports_v1";

// Parse inline formatting (bold text with **) for category reports
const parseInlineFormatting = (text) => {
  if (!text || typeof text !== "string") return text;

  const result = [];
  let remaining = text;
  let keyCounter = 0;

  while (remaining.length > 0) {
    // Check for bold (**text**)
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    if (boldMatch) {
      // Add text before the match
      if (boldMatch.index > 0) {
        result.push(remaining.slice(0, boldMatch.index));
      }
      // Add the bold text
      result.push(
        <strong key={keyCounter++} className="font-bold text-adhd-yellow">
          {boldMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }

    // No more matches, add the rest
    result.push(remaining);
    break;
  }

  return result.length > 0 ? result : text;
};

const QuestionScreen = ({
  firstName,
  email,
  answers,
  setAnswers,
  currentQuestion,
  setCurrentQuestion,
  currentCategory,
  setCurrentCategory,
  savedStatus,
  onSubmit,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [followUpQuestion, setFollowUpQuestion] = useState("");
  const [isCheckingContext, setIsCheckingContext] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // New states for improved follow-up system
  const [hasEnoughDetail, setHasEnoughDetail] = useState(false);
  const [contextCheckCount, setContextCheckCount] = useState(0);
  const [lastCheckedContent, setLastCheckedContent] = useState("");
  const checkTimeoutRef = useRef(null);

  // Category reports
  const [categoryReports, setCategoryReports] = useState({});
  const [isGeneratingCategoryReport, setIsGeneratingCategoryReport] =
    useState(false);
  const [showCategoryReport, setShowCategoryReport] = useState(false);
  const [currentCategoryReport, setCurrentCategoryReport] = useState(null);

  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Submission lock to prevent duplicate submissions
  const isSubmittingRef = useRef(false);

  // ---- Local Storage: category reports ----
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_CATEGORY_REPORTS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed === "object") setCategoryReports(parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        LS_CATEGORY_REPORTS_KEY,
        JSON.stringify(categoryReports)
      );
    } catch {
      // ignore
    }
  }, [categoryReports]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 400) + "px";
    }
  }, [answers, currentQuestion]);

  // Check if we're at the last question of a category
  const isLastQuestionOfCategory = () => {
    const category = categories[currentCategory];
    return currentQuestion === category.endIndex;
  };

  // Check if we're on the last category (Day 5)
  const isLastCategory = () => {
    return currentCategory === categories.length - 1;
  };

  // Check if all questions in current category are answered
  const isCategoryComplete = () => {
    const category = categories[currentCategory];
    for (let i = category.startIndex; i <= category.endIndex; i++) {
      if (!answers[i]?.trim()) return false;
    }
    return true;
  };

  // Generate category report
  const generateCategoryReport = async () => {
    const category = categories[currentCategory];

    // Gather responses for this category
    const categoryResponses = [];
    for (let i = category.startIndex; i <= category.endIndex; i++) {
      const question = questionsConfig[i];
      if (question && answers[i]) {
        categoryResponses.push({
          question: question.question,
          answer: answers[i],
        });
      }
    }

    setIsGeneratingCategoryReport(true);

    try {
      const response = await fetch("/api/generate-category-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName,
          categoryName: category.name,
          categoryPrompt: category.reportPrompt,
          responses: categoryResponses,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const report = data.report;
        setCategoryReports((prev) => ({
          ...prev,
          [currentCategory]: report,
        }));
        setCurrentCategoryReport(report);
        setShowCategoryReport(true);
      } else {
        console.error("Failed to generate category report:", data.error);
      }
    } catch (error) {
      console.error("Error generating category report:", error);
    } finally {
      setIsGeneratingCategoryReport(false);
    }
  };

  // Improved continuous context checking
  const checkContextAndGetFollowUp = useCallback(async (forceCheck = false) => {
    const currentAnswer = answers[currentQuestion] || "";

    // Don't check if answer is very short
    if (!currentAnswer || currentAnswer.trim().length < 30) {
      setHasEnoughDetail(false);
      setShowFollowUp(false);
      setLastCheckedContent("");
      return;
    }

    // Skip if we already checked this exact content (unless forced)
    if (!forceCheck && currentAnswer === lastCheckedContent) {
      return;
    }

    setIsCheckingContext(true);
    setLastCheckedContent(currentAnswer);

    try {
      const response = await fetch("/api/check-context", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          questionId: currentQuestion + 1,
          question: questionsConfig[currentQuestion]?.question,
          answer: currentAnswer,
          previousAnswers: Object.keys(answers)
            .filter(
              (key) => answers[key]?.trim() && key !== String(currentQuestion)
            )
            .map((key) => ({
              question: questionsConfig[key]?.question,
              answer: answers[key],
            }))
            .slice(-2),
          allQuestions: questionsConfig,
        }),
      });

      const data = await response.json();

      if (data.needsMoreContext && data.followUpQuestion) {
        setFollowUpQuestion(data.followUpQuestion);
        setShowFollowUp(true);
        setHasEnoughDetail(false);
      } else {
        setShowFollowUp(false);
        setFollowUpQuestion("");
        setHasEnoughDetail(true);
      }

      setContextCheckCount((prev) => prev + 1);
    } catch (error) {
      console.error("Error checking context:", error);
      setHasEnoughDetail(true);
    } finally {
      setIsCheckingContext(false);
    }
  }, [answers, currentQuestion, lastCheckedContent]);

  // Continuous monitoring effect - simplified and fixed
  useEffect(() => {
    // Clear any existing timeout
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    const currentAnswer = answers[currentQuestion] || "";

    // If no answer, reset states
    if (!currentAnswer) {
      setHasEnoughDetail(false);
      setShowFollowUp(false);
      setFollowUpQuestion("");
      setContextCheckCount(0);
      setLastCheckedContent("");
      return;
    }

    // If content changed from what we last checked, schedule a new check
    if (currentAnswer !== lastCheckedContent) {
      checkTimeoutRef.current = setTimeout(() => {
        checkContextAndGetFollowUp(true);
      }, 5000); // 5 seconds as requested
    }

    // Cleanup
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [answers, currentQuestion, lastCheckedContent, checkContextAndGetFollowUp]);

  // Reset states when question changes
  useEffect(() => {
    setHasEnoughDetail(false);
    setShowFollowUp(false);
    setFollowUpQuestion("");
    setContextCheckCount(0);
    setLastCheckedContent("");
  }, [currentQuestion]);

  const handleInputChange = (value) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion]: value }));

    // If they typed a lot more (50+ chars), trigger check sooner
    const currentLength = (answers[currentQuestion] || "").length;
    if (Math.abs(value.length - currentLength) > 50) {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
      checkTimeoutRef.current = setTimeout(() => {
        checkContextAndGetFollowUp(true);
      }, 3000); // Check sooner for large changes
    }
  };

  const progress = (
    (Object.keys(answers).filter((key) => answers[key]?.trim()).length /
      questionsConfig.length) *
    100
  ).toFixed(0);

  const getCategoryProgress = (category) => {
    const ids = [];
    for (let i = category.startIndex; i <= category.endIndex; i++) ids.push(i);
    const done = ids.filter((q) => answers[q]?.trim()).length;
    return (done / ids.length) * 100;
  };

  const handleNext = () => {
    const currentAnswer = answers[currentQuestion] || "";
    const wordCount = currentAnswer.split(" ").filter((w) => w).length;

    if (wordCount < 5) {
      setSubmitStatus("Please answer the question before continuing.");
      setTimeout(() => setSubmitStatus(""), 3000);
      return;
    }

    // Check if this is the last question of a category (but NOT the last category - Day 5)
    // For Day 5, we skip the category report and go directly to full report submission
    if (
      isLastQuestionOfCategory() &&
      isCategoryComplete() &&
      !categoryReports[currentCategory] &&
      !isLastCategory() // Skip category report for the last category
    ) {
      generateCategoryReport();
    } else if (currentQuestion < questionsConfig.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowCategoryReport(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowCategoryReport(false);
    }
  };

  const handleCategoryChange = (categoryIndex) => {
    const category = categories[categoryIndex];
    setCurrentQuestion(category.startIndex);
    setCurrentCategory(categoryIndex);
    setShowCategoryDropdown(false);
    setShowCategoryReport(false);
  };

  const handleContinueToNextCategory = () => {
    if (currentCategory < categories.length - 1) {
      handleCategoryChange(currentCategory + 1);
    } else {
      setShowCategoryReport(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach((t) => t.stop());
        setAudioStream(null);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Please allow microphone access to use voice input.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      formData.append("model", "whisper-1");

      const response = await fetch("/api/transcribe", {
        method: "POST",
        body: formData,
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }

      if (!response.ok) {
        const msg =
          data?.error?.message ||
          data?.error ||
          `Transcription failed (${response.status})`;
        throw new Error(msg);
      }

      const transcript = data?.text || "";
      const newAnswer =
        (answers[currentQuestion] || "") +
        (answers[currentQuestion] ? " " : "") +
        transcript;

      setAnswers((prev) => ({
        ...prev,
        [currentQuestion]: newAnswer,
      }));

      // Force a context check after voice input
      setTimeout(() => {
        checkContextAndGetFollowUp(true);
      }, 2000);
    } catch (error) {
      console.error("Transcription error:", error);
      alert(error.message || "Error transcribing audio.");
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleSubmit = async () => {
    // Prevent duplicate submissions using ref (survives re-renders)
    if (isSubmittingRef.current) {
      console.log("Submission already in progress, ignoring duplicate click");
      return;
    }
    
    // Lock submission immediately
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitStatus("");

    const answered = Object.keys(answers).filter((k) => answers[k]?.trim());
    if (answered.length < questionsConfig.length) {
      setSubmitStatus("Please answer all questions before submitting.");
      setIsSubmitting(false);
      isSubmittingRef.current = false; // Reset lock on validation error
      return;
    }

    const formattedData = {
      firstName,
      email,
      responses: questionsConfig.map((q, index) => ({
        question_id: q.id,
        question: q.question,
        answer: answers[index] || "",
      })),
      categoryReports,
    };

    try {
      // Show loading screen - keep isSubmitting true as well for button protection
      setIsGeneratingReport(true);

      // Call the generate-report API (this takes 45+ seconds)
      const generateResponse = await fetch("/api/generate-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedData),
      });

      const generateData = await generateResponse.json();

      if (!generateResponse.ok || !generateData.success) {
        throw new Error(generateData.error || "Failed to generate report");
      }

      // Pass the generated report to parent
      const result = await onSubmit({
        ...formattedData,
        report: generateData.report,
      });

      if (!result.success) {
        setSubmitStatus(result.error);
        setIsGeneratingReport(false);
        setIsSubmitting(false);
        isSubmittingRef.current = false; // Reset lock on error
      }
      // If successful, onSubmit will handle the screen transition
      // Don't reset the ref - submission was successful
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus(error.message || "An error occurred. Please try again.");
      setIsGeneratingReport(false);
      setIsSubmitting(false);
      isSubmittingRef.current = false; // Reset lock on error so user can retry
    }
  };

  // Show loading screen if generating report
  if (isGeneratingReport) {
    return <LoadingScreen firstName={firstName} />;
  }

  return (
    <div className="min-h-screen gradient-bg py-6 px-4 page-transition">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 animate-fadeIn">
          <div className="text-center mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-100 mb-1">
              Hi {firstName}, let's make 2026 your best year yet
            </h1>
            <p className="text-gray-300 text-sm md:text-base">
              Be honest and specific • Your progress is saved automatically
            </p>
          </div>

          {/* Overall Progress */}
          <div className="glass rounded-2xl p-4 subtle-shadow">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <ProgressRing progress={parseInt(progress, 10)} />
                <div>
                  <p className="text-sm font-bold text-gray-100">
                    {progress}% Complete
                  </p>
                  <p className="text-xs text-gray-400">
                    {
                      Object.keys(answers).filter((key) => answers[key]?.trim())
                        .length
                    }{" "}
                    of {questionsConfig.length} questions answered
                  </p>
                </div>
              </div>

              {/* Status + Rewatch */}
              <div className="flex items-center gap-3">
                {savedStatus && (
                  <div className="flex items-center gap-1.5 text-adhd-green text-sm animate-fadeIn">
                    <CheckCircle size={14} />
                    <span className="font-semibold">Saved</span>
                  </div>
                )}

                <button
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent("goToWelcome"))
                  }
                  className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg glass hover:glass-light text-gray-300 hover:text-gray-100 transition-colors border border-adhd-yellow/20"
                  title="Back to introduction"
                >
                  <ArrowLeft size={16} />
                  <span className="hidden md:inline">Back to Intro</span>
                  <span className="md:hidden">Intro</span>
                </button>

                {categoryReports[currentCategory] && (
                  <button
                    onClick={() => {
                      setCurrentCategoryReport(
                        categoryReports[currentCategory]
                      );
                      setShowCategoryReport(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-gradient-to-r from-adhd-green/10 to-adhd-green/10 border border-adhd-green/30 text-adhd-green hover:from-adhd-green/20 hover:to-adhd-green/20 transition-all font-semibold"
                    title="View insights for this section"
                  >
                    <FileText size={16} />
                    <span>View insights</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category Selector */}
        <div className="mb-6 animate-slideUp">
          <div className="glass rounded-2xl p-1 subtle-shadow">
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-full p-4 flex items-center justify-between glass-light hover:glass rounded-xl transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-adhd-yellow/20 to-adhd-orange/20 border border-adhd-yellow/30 rounded-lg">
                  {(() => {
                    const Icon = categories[currentCategory].icon;
                    return <Icon size={20} className="text-adhd-yellow" />;
                  })()}
                </div>
                <div className="text-left">
                  <p className="text-xs text-gray-400 font-semibold">
                    CURRENT SECTION
                  </p>
                  <p className="font-bold text-gray-100">
                    {categories[currentCategory].name}
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`text-gray-400 transform transition-transform ${
                  showCategoryDropdown ? "rotate-180" : ""
                }`}
                size={20}
              />
            </button>

            {showCategoryDropdown && (
              <div className="p-2 space-y-1 animate-fadeIn">
                {categories.map((category, index) => {
                  const Icon = category.icon;
                  const categoryProgress = getCategoryProgress(category);
                  const isActive = currentCategory === index;
                  const isComplete = categoryProgress === 100;
                  const hasReport = Boolean(categoryReports[index]);

                  return (
                    <button
                      key={category.name || index}
                      onClick={() => handleCategoryChange(index)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-adhd-yellow/10 to-adhd-orange/10 border-2 border-adhd-yellow/30"
                          : "glass-light hover:glass border-2 border-transparent"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg ${
                          isActive
                            ? "bg-adhd-yellow"
                            : isComplete
                            ? "bg-adhd-green/20 border border-adhd-green/30"
                            : "glass"
                        }`}
                      >
                        <Icon
                          size={18}
                          className={
                            isActive
                              ? "text-black"
                              : isComplete
                              ? "text-adhd-green"
                              : "text-gray-400"
                          }
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-bold text-gray-100">
                          {category.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {category.description}
                        </p>
                      </div>
                      <div className="text-right">
                        {hasReport && (
                          <FileText
                            size={14}
                            className="text-adhd-green mb-1"
                          />
                        )}
                        <p className="text-xs font-bold text-gray-300">
                          {Math.round(categoryProgress)}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {category.endIndex - category.startIndex + 1}{" "}
                          questions
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Content panel */}
        {showCategoryReport && currentCategoryReport ? (
          // Category Report Display
          <div className="glass rounded-3xl shadow-xl p-6 md:p-8 animate-slideUp">
            <div className="mb-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-adhd-yellow/20 to-adhd-orange/20 border border-adhd-yellow/30 rounded-2xl flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-adhd-yellow" />
              </div>
              <h2 className="text-2xl font-bold text-gray-100 mb-2">
                Insights from {categories[currentCategory].name}
              </h2>
              <p className="text-gray-300">
                Here's what we noticed from your responses
              </p>
            </div>

            <div className="prose prose-invert max-w-none mb-8">
              <div className="glass-light rounded-2xl p-6 border border-adhd-yellow/10">
                {currentCategoryReport.content
                  .split("\n\n")
                  .map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-gray-300 mb-4 last:mb-0 leading-relaxed"
                    >
                      {parseInlineFormatting(paragraph)}
                    </p>
                  ))}
              </div>
            </div>

            <div className="flex justify-center gap-4">
              {currentCategory < categories.length - 1 ? (
                <button
                  onClick={handleContinueToNextCategory}
                  className="flex items-center gap-2 px-6 py-3 premium-button text-black rounded-xl transition-all font-bold hover-lift"
                >
                  <span>
                    Continue to {categories[currentCategory + 1].name}
                  </span>
                  <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  onClick={() => setShowCategoryReport(false)}
                  className="flex items-center gap-2 px-6 py-3 glass border border-adhd-yellow/30 text-gray-100 rounded-xl hover:glass-light transition-all font-bold hover-lift"
                >
                  <span>Continue to Final Submission</span>
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Question Card */}
            <div className="glass rounded-3xl shadow-xl p-6 md:p-8 mb-6 animate-slideUp mobile-full-width">
              {/* Question Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-bold text-adhd-yellow">
                    Question{" "}
                    {currentQuestion -
                      categories[currentCategory].startIndex +
                      1}{" "}
                    of{" "}
                    {categories[currentCategory].endIndex -
                      categories[currentCategory].startIndex +
                      1}
                  </p>

                  {/* Question Navigation Pills */}
                  <div className="flex gap-1.5">
                    {(() => {
                      const currentCat = categories[currentCategory];
                      const questions = [];
                      for (
                        let i = currentCat.startIndex;
                        i <= currentCat.endIndex;
                        i++
                      ) {
                        questions.push(i);
                      }
                      return questions.map((index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setCurrentQuestion(index);
                          }}
                          className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                            currentQuestion === index
                              ? "bg-adhd-yellow text-black shadow-md"
                              : answers[index]?.trim()
                              ? "bg-adhd-green/20 text-adhd-green border border-adhd-green/30"
                              : "glass text-gray-400"
                          }`}
                        >
                          {index - currentCat.startIndex + 1}
                        </button>
                      ));
                    })()}
                  </div>
                </div>

                <h2 className="text-xl md:text-2xl font-bold text-gray-100 leading-relaxed">
                  {questionsConfig[currentQuestion]?.question ||
                    "Loading question..."}
                </h2>
              </div>

              <div className="divider mb-6"></div>

              {/* Enhanced Answer Input */}
              <div className="mb-6">
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={answers[currentQuestion] || ""}
                    onChange={(e) => handleInputChange(e.target.value)}
                    placeholder="Share your thoughts here... You can type or use voice recording below."
                    className={`w-full min-h-[160px] p-4 border-2 rounded-2xl resize-none transition-all text-gray-100 leading-relaxed placeholder-gray-500 glass-light ${
                      hasEnoughDetail
                        ? "border-adhd-green bg-adhd-green/5"
                        : showFollowUp
                        ? "border-adhd-orange bg-adhd-orange/5"
                        : isCheckingContext
                        ? "analyzing-border"
                        : "border-adhd-yellow/20 focus:border-adhd-yellow"
                    }`}
                    style={{ fontSize: "16px" }}
                  />

                  {/* Status Indicators */}
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    {isCheckingContext && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-adhd-yellow/20 to-adhd-orange/20 border border-adhd-yellow/30 rounded-full animate-fadeIn">
                        <Loader2 className="w-3 h-3 animate-spin text-adhd-yellow" />
                        <span className="text-xs font-bold text-adhd-yellow">
                          Analyzing...
                        </span>
                      </div>
                    )}

                    {!isCheckingContext && hasEnoughDetail && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-adhd-green/20 border border-adhd-green/30 rounded-full animate-fadeIn">
                        <CheckCircle className="w-3 h-3 text-adhd-green" />
                        <span className="text-xs font-bold text-adhd-green">
                          Great detail!
                        </span>
                      </div>
                    )}

                    {!isCheckingContext && showFollowUp && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-adhd-orange/20 border border-adhd-orange/30 rounded-full animate-fadeIn">
                        <AlertCircle className="w-3 h-3 text-adhd-orange" />
                        <span className="text-xs font-bold text-adhd-orange">
                          Consider adding more
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Word count indicator */}
                <div className="flex justify-between items-center mt-2">
                  <div className="text-xs text-gray-400">
                    {
                      (answers[currentQuestion] || "")
                        .split(" ")
                        .filter((w) => w).length
                    }{" "}
                    words
                  </div>
                  {contextCheckCount > 0 && (
                    <div className="text-xs text-gray-400">
                      Checked {contextCheckCount} time
                      {contextCheckCount > 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced AI Follow-up */}
              {showFollowUp && followUpQuestion && (
                <div className="mb-6 animate-slideDown follow-up-container">
                  <div className="p-5 glass-light rounded-2xl border-2 border-adhd-orange/30 bg-gradient-to-br from-adhd-orange/5 to-adhd-yellow/5 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="p-2.5 bg-gradient-to-br from-adhd-orange/20 to-adhd-yellow/20 border border-adhd-orange/30 rounded-xl shadow-sm flex-shrink-0">
                        <Sparkles size={20} className="text-adhd-orange" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-sm font-bold text-adhd-orange">
                            Optional: Go Deeper
                          </p>
                          <span className="text-xs text-adhd-yellow bg-adhd-yellow/10 border border-adhd-yellow/30 px-2 py-0.5 rounded-full font-semibold">
                            Helps create better insights
                          </span>
                        </div>
                        <p className="text-gray-200 leading-relaxed font-medium">
                          {followUpQuestion}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          You can continue to the next question or add more
                          detail here.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Success message when enough detail is provided */}
              {hasEnoughDetail &&
                !showFollowUp &&
                answers[currentQuestion]?.trim()?.length > 50 && (
                  <div className="mb-6 animate-slideDown">
                    <div className="p-4 glass-light rounded-2xl border-2 border-adhd-green/30 bg-gradient-to-br from-adhd-green/5 to-adhd-green/5 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-adhd-green/20 to-adhd-green/10 border border-adhd-green/30 rounded-xl shadow-sm">
                          <CheckCircle size={20} className="text-adhd-green" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-bold text-adhd-green">
                            Perfect! You've shared great detail
                          </p>
                          <p className="text-xs text-gray-300 mt-0.5">
                            Your answer will help us create meaningful insights
                            for you.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Voice Recording */}
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row items-center gap-3">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isTranscribing}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all hover-lift ${
                      isRecording
                        ? "bg-adhd-red text-white hover:bg-adhd-red-bright breathing"
                        : "premium-button text-black"
                    } ${isTranscribing ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRecording ? (
                      <>
                        <MicOff size={18} />
                        <span>Stop Recording</span>
                      </>
                    ) : (
                      <>
                        <Mic size={18} />
                        <span>Record Answer</span>
                      </>
                    )}
                  </button>

                  {isTranscribing && (
                    <div className="flex items-center gap-2 text-adhd-yellow animate-fadeIn">
                      <Loader2 className="animate-spin" size={18} />
                      <span className="text-sm font-semibold">
                        Transcribing...
                      </span>
                    </div>
                  )}
                </div>

                {isRecording && audioStream && (
                  <div className="animate-fadeIn">
                    <VoiceVisualizer
                      isRecording={isRecording}
                      stream={audioStream}
                    />
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className="flex items-center gap-2 px-5 py-2.5 text-gray-300 hover:text-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-semibold"
                >
                  <ChevronLeft size={18} />
                  <span>Previous</span>
                </button>

                {/* Show "Get Section Insights" only for categories 1-4, NOT for Day 5 (last category) */}
                {isLastQuestionOfCategory() &&
                isCategoryComplete() &&
                !categoryReports[currentCategory] &&
                !isLastCategory() ? (
                  <button
                    onClick={generateCategoryReport}
                    disabled={isGeneratingCategoryReport}
                    className="flex items-center gap-2 px-5 py-2.5 premium-button text-black rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold hover-lift"
                  >
                    {isGeneratingCategoryReport ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        <span>Generating Insights...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        <span>Get Section Insights</span>
                      </>
                    )}
                  </button>
                ) : currentQuestion === questionsConfig.length - 1 ? (
                  // On last question, show Submit button directly instead of disabled Next
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      isGeneratingReport ||
                      Object.keys(answers).filter((key) => answers[key]?.trim())
                        .length < questionsConfig.length
                    }
                    className="flex items-center gap-2 px-5 py-2.5 premium-button text-black rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold hover-lift"
                  >
                    {isSubmitting || isGeneratingReport ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        <span>Preparing...</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>Submit & Get Profile</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 px-5 py-2.5 glass border border-adhd-yellow/30 text-gray-100 rounded-xl hover:glass-light transition-all font-bold hover-lift"
                  >
                    <span>Next</span>
                    <ChevronRight size={18} />
                  </button>
                )}
              </div>

              {/* Submit status messages */}
              {submitStatus && (
                <div className="mt-4 p-3 bg-adhd-orange/10 border border-adhd-orange/30 rounded-xl text-center text-sm text-adhd-orange animate-fadeIn font-semibold">
                  {submitStatus}
                </div>
              )}
            </div>

            {/* Submit Section */}
            {currentQuestion === questionsConfig.length - 1 && (
              <div className="glass rounded-3xl shadow-xl p-6 md:p-8 animate-fadeIn mobile-full-width">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-adhd-green/20 to-adhd-green/10 border border-adhd-green/30 rounded-2xl flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-adhd-green" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">
                    You're all done!
                  </h3>
                  <p className="text-gray-300 max-w-md mx-auto">
                    Review your answers using the navigation above, then submit
                    to receive your personalized Profile via email.
                  </p>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    isGeneratingReport ||
                    Object.keys(answers).filter((key) => answers[key]?.trim())
                      .length < questionsConfig.length
                  }
                  className="w-full premium-button text-black rounded-xl py-4 px-8 font-bold text-lg hover-lift disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {isSubmitting || isGeneratingReport ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      <span>Preparing Your Report...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Submit & Get My Full Profile</span>
                    </>
                  )}
                </button>

                {submitStatus && (
                  <div
                    className={`mt-4 p-4 rounded-xl text-center font-bold animate-fadeIn ${
                      submitStatus.includes("Error") ||
                      submitStatus.includes("Please")
                        ? "bg-adhd-red/10 border border-adhd-red/30 text-adhd-red"
                        : "bg-adhd-orange/10 border border-adhd-orange/30 text-adhd-orange"
                    }`}
                  >
                    {submitStatus}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionScreen;
